import { useState, useEffect, useId, useMemo } from 'react';
import { noop } from 'utils/noop';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { InputText } from 'ui/inputs/inputText';
import { FormField } from 'ui/formField';
import { Select } from 'ui/selectv2/Select';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES } from 'ui/modal';
import { spacing } from 'ui/theme';
import {
  BUILD_COLLECTION_TEXT,
  CATALOG_COLLECTION_NAME,
  CATALOG_COLLECTIONS_APP_URL,
  CREATE_COLLECTION_TEXT,
  EDIT_COLLECTION_TEXT,
  NEW_COLLECTION_ID,
  SAVE_COLLECTION_TEXT,
  VIEW_COLLECTION_TEXT,
} from 'modules/catalog/catalogConstants';
import { submitCreateCollectionModal } from 'modules/catalog/collections/actions';
import {
  changeCatalog,
  changeCollectionDescription,
  changeCollectionName,
  updateCollection,
  resetCollectionBuilder,
} from 'modules/catalog/collectionBuilder/actions';
import { fetchCatalogNames } from 'modules/catalog/actions';
import {
  selectCatalog,
  selectDescription,
  selectName,
} from 'modules/catalog/collectionBuilder/selectors';
import { selectCatalogsAsDropdownOptions } from 'modules/catalogTable/selectors';
import { Link } from 'react-router-dom';
import { isAlphaNumericDashesSpaces, noLeadingSpaces, noTrailingSpaces } from 'utils/validation';
import { track } from 'modules/segment/segmentEmitter';
import { selectUserContextForSegmentTracking } from 'core/userContext/selectors';

const UL = styled.ul`
  display: flex;
  padding: 0;
  flex-direction: column;
  gap: ${spacing.quarter};
`;

// type Props = {
//   /** Pass this value to initialize selected catalog value in dropdown. */
//   catalog: string,
//   /** Pass this value if this is an edit modal. */
//   collection: object,
//   /** Function to close modal */
//   closeModal: () => {},
//   /** Boolean whether to fetch the catalogs */
//   shouldFetchCatalogs: boolean,
//   /** Manually open modal without calling state. */
//   isOpen: boolean,
//   /** Whether this is an edit modal for the collection */
//   isEdit: boolean,
// };

export const CreateCollectionModal = ({
  catalog,
  collection,
  isOpen = false,
  shouldFetchCatalogs = false,
  closeModal = noop,
  isEdit = false,
}) => {
  const modalId = useId();
  const nameInputId = useId();
  const catalogInputId = useId();
  const descriptionInputId = useId();
  const dispatch = useDispatch();

  const currentCatalog = useSelector(selectCatalog);
  const catalogOptions = useSelector(selectCatalogsAsDropdownOptions);
  const description = useSelector(selectDescription);
  const collectionName = useSelector(selectName);
  const userState = useSelector(selectUserContextForSegmentTracking);

  const [errorMessages, setErrorMessages] = useState([]);
  const [focus, setFocus] = useState(false);
  const [trailingSpaces, setTrailingSpaces] = useState(false);
  const [name, setName] = useState(collectionName);

  const selectedCatalogOption = useMemo(() => {
    return catalogOptions.find((option) => option.value === currentCatalog) || catalogOptions[0];
  }, [catalogOptions, currentCatalog]);

  useEffect(() => {
    if (shouldFetchCatalogs) {
      dispatch(fetchCatalogNames());
    }
  }, [dispatch, shouldFetchCatalogs]);

  useEffect(() => {
    if (isOpen) {
      track('Started Catalog Collection Creation', { ...userState });
    }
  }, [isOpen, userState]);

  useEffect(() => {
    if (catalog !== currentCatalog && !isOpen) {
      dispatch(changeCatalog(catalog, false));
      setErrorMessages([]);
      setFocus(false);
      setTrailingSpaces(false);
    }
  }, [catalog, currentCatalog, isOpen, dispatch]);

  /**
   * Checks for validation errors for collection name.
   */
  const checkForErrors = (value, hasTrailingSpaces) => {
    const currentErrorMessages = [...isAlphaNumericDashesSpaces(value), ...noLeadingSpaces(value)];
    if (focus && !trailingSpaces && !hasTrailingSpaces) {
      setErrorMessages(currentErrorMessages);
    } else {
      setErrorMessages([...currentErrorMessages, ...noTrailingSpaces(value)]);
    }
  };

  const handleBlur = () => {
    const hasTrailingSpaces = noTrailingSpaces(name).length > 0;
    setFocus(false);
    setTrailingSpaces(hasTrailingSpaces);
    checkForErrors(name, hasTrailingSpaces);
  };

  const handleChangeCollectionName = (value) => {
    setName(value);
    dispatch(changeCollectionName(value));
    checkForErrors(value);
  };

  const handleClose = () => {
    dispatch(resetCollectionBuilder());
    closeModal();
  };

  const handleEditCollection = () => {
    const { id, searchQuery } = collection;
    dispatch(
      updateCollection(id, { ...collection, ...searchQuery }, () =>
        dispatch(resetCollectionBuilder()),
      ),
    );
    handleClose();
  };

  const handleCreateCollection = () => {
    dispatch(submitCreateCollectionModal());
  };

  const hasError = errorMessages.length > 0;

  return isOpen ? (
    <Modal
      id={modalId}
      isModalOpen={isOpen}
      onClose={handleClose}
      size={MODAL_SIZES.SMALL}
      data-test="CreateCollectionModal"
    >
      <ModalHeader>{isEdit ? EDIT_COLLECTION_TEXT : CREATE_COLLECTION_TEXT}</ModalHeader>
      <ModalBody>
        <FormField
          inputId={nameInputId}
          labelText="Name"
          hasError={hasError}
          errorText={
            <>
              <p>The {CATALOG_COLLECTION_NAME} name:</p>
              <UL>
                {errorMessages.map((errorMessage) => (
                  <li key={errorMessage}>{errorMessage}</li>
                ))}
              </UL>
            </>
          }
        >
          <InputText
            data-test="createCollectionModal-name-input-field"
            id={nameInputId}
            onBlur={handleBlur}
            onChange={handleChangeCollectionName}
            onFocus={() => setFocus(true)}
            placeholder={`${CATALOG_COLLECTION_NAME} Name`}
            value={name}
          />
        </FormField>
        {!isEdit && (
          <FormField
            labelText="Catalog"
            inputId={catalogInputId}
            data-test="createCollectionModal-catalog-dropdown"
          >
            <Select
              usePortal
              onSelect={([{ value }]) => dispatch(changeCatalog(value))}
              value={[selectedCatalogOption]}
              options={catalogOptions}
            />
          </FormField>
        )}
        <FormField labelText="Description" inputId={descriptionInputId}>
          <InputText
            data-test="createCollectionModal-description-input-field"
            id={descriptionInputId}
            onChange={(value) => dispatch(changeCollectionDescription(value))}
            placeholder="Add a description"
            value={description}
          />
        </FormField>
      </ModalBody>
      <ModalFooter onClose={handleClose}>
        {!isEdit ? (
          <Link to={{ pathname: `${CATALOG_COLLECTIONS_APP_URL}/${NEW_COLLECTION_ID}` }}>
            <PrimaryButton isDisabled={!name || hasError} onClick={handleCreateCollection}>
              {BUILD_COLLECTION_TEXT}
            </PrimaryButton>
          </Link>
        ) : (
          <>
            <Link to={{ pathname: `${CATALOG_COLLECTIONS_APP_URL}/${collection.id}` }}>
              <SecondaryButton onClick={handleClose}>{VIEW_COLLECTION_TEXT}</SecondaryButton>
            </Link>
            <PrimaryButton isDisabled={!name || hasError} onClick={handleEditCollection}>
              {SAVE_COLLECTION_TEXT}
            </PrimaryButton>
          </>
        )}
      </ModalFooter>
    </Modal>
  ) : null;
};
